/**
 * Migration Script: Realtime Database to Firestore
 * 
 * This script helps migrate data from Firebase Realtime Database to Firestore
 * Run this once to transfer all existing data to the new ca_admin collection
 */

import { getDatabase, ref, get } from "firebase/database";
import { getFirestore, doc, setDoc, writeBatch } from "firebase/firestore";
import app from "../firebase/config";

const database = getDatabase(app);
const db = getFirestore(app);

export const migrateToFirestore = async () => {
  const results = {
    success: [],
    errors: [],
    summary: {
      caFirms: 0,
      admins: 0,
      total: 0
    }
  };

  try {
    console.log("Starting migration from Realtime Database to Firestore...");

    // Migrate CA Firms
    console.log("Migrating CA Firms...");
    const caFirmsRef = ref(database, "CA Firm");
    const caFirmsSnapshot = await get(caFirmsRef);

    if (caFirmsSnapshot.exists()) {
      const caFirmsData = caFirmsSnapshot.val();
      const batch = writeBatch(db);
      let batchCount = 0;

      for (const [key, value] of Object.entries(caFirmsData)) {
        try {
          const docRef = doc(db, "ca_admin", key);
          batch.set(docRef, value);
          batchCount++;
          results.summary.caFirms++;

          // Firestore batch limit is 500 operations
          if (batchCount >= 500) {
            await batch.commit();
            console.log(`Committed batch of ${batchCount} CA firms`);
            batchCount = 0;
          }

          results.success.push(`CA Firm: ${key}`);
        } catch (error) {
          console.error(`Error migrating CA Firm ${key}:`, error);
          results.errors.push({ type: "CA Firm", id: key, error: error.message });
        }
      }

      // Commit remaining items
      if (batchCount > 0) {
        await batch.commit();
        console.log(`Committed final batch of ${batchCount} CA firms`);
      }
    } else {
      console.log("No CA Firms found in Realtime Database");
    }

    // Migrate Admin Users
    console.log("Migrating Admin Users...");
    const adminsRef = ref(database, "admin");
    const adminsSnapshot = await get(adminsRef);

    if (adminsSnapshot.exists()) {
      const adminsData = adminsSnapshot.val();
      const batch = writeBatch(db);
      let batchCount = 0;

      for (const [key, value] of Object.entries(adminsData)) {
        try {
          const docRef = doc(db, "ca_admin", key);
          batch.set(docRef, value);
          batchCount++;
          results.summary.admins++;

          if (batchCount >= 500) {
            await batch.commit();
            console.log(`Committed batch of ${batchCount} admins`);
            batchCount = 0;
          }

          results.success.push(`Admin: ${key}`);
        } catch (error) {
          console.error(`Error migrating Admin ${key}:`, error);
          results.errors.push({ type: "Admin", id: key, error: error.message });
        }
      }

      // Commit remaining items
      if (batchCount > 0) {
        await batch.commit();
        console.log(`Committed final batch of ${batchCount} admins`);
      }
    } else {
      console.log("No Admin users found in Realtime Database");
    }

    results.summary.total = results.summary.caFirms + results.summary.admins;

    console.log("Migration completed!");
    console.log(`Total migrated: ${results.summary.total}`);
    console.log(`CA Firms: ${results.summary.caFirms}`);
    console.log(`Admins: ${results.summary.admins}`);
    console.log(`Errors: ${results.errors.length}`);

    return results;
  } catch (error) {
    console.error("Migration failed:", error);
    results.errors.push({ type: "General", error: error.message });
    return results;
  }
};

// Function to verify migration
export const verifyMigration = async () => {
  try {
    console.log("Verifying migration...");

    // Check Realtime Database counts
    const caFirmsRef = ref(database, "CA Firm");
    const adminsRef = ref(database, "admin");

    const [caFirmsSnapshot, adminsSnapshot] = await Promise.all([
      get(caFirmsRef),
      get(adminsRef)
    ]);

    const rtdbCounts = {
      caFirms: caFirmsSnapshot.exists() ? Object.keys(caFirmsSnapshot.val()).length : 0,
      admins: adminsSnapshot.exists() ? Object.keys(adminsSnapshot.val()).length : 0
    };

    // Check Firestore counts
    const { collection, getDocs } = await import("firebase/firestore");
    const firestoreSnapshot = await getDocs(collection(db, "ca_admin"));
    
    let firestoreCounts = {
      caFirms: 0,
      admins: 0
    };

    firestoreSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.firmName) {
        firestoreCounts.caFirms++;
      } else if (data.role === "super_admin") {
        firestoreCounts.admins++;
      }
    });

    console.log("Verification Results:");
    console.log("Realtime Database:", rtdbCounts);
    console.log("Firestore:", firestoreCounts);

    const isMatch = 
      rtdbCounts.caFirms === firestoreCounts.caFirms &&
      rtdbCounts.admins === firestoreCounts.admins;

    if (isMatch) {
      console.log("✅ Migration verified successfully!");
    } else {
      console.log("⚠️ Migration counts don't match. Please review.");
    }

    return {
      rtdbCounts,
      firestoreCounts,
      isMatch
    };
  } catch (error) {
    console.error("Verification failed:", error);
    return { error: error.message };
  }
};
